import { jwtDecode } from 'jwt-decode';
import type { GetTokenResponse } from '@/types/auth';
import {
  sanitizeTokenRecoveryDiagnostic,
  type TokenRecoveryDiagnostic
} from './tokenRecoveryDiagnostics';

export type TokenRequestOperation = 'bootstrap' | 'refresh';

export const TOKEN_REQUEST_PATHS: Record<TokenRequestOperation, string> = {
  bootstrap: '/digit-chat/tokens',
  refresh: '/digit-chat/tokens/refresh'
};

type FailureStage =
  | 'transport'
  | 'http'
  | 'response'
  | 'access_token'
  | 'jwt'
  | 'state'
  | 'coordination';
type TransportFailure = 'watchdog_timeout' | 'cancelled' | 'no_response';
export type TokenJwtFailure =
  | 'expired_or_insufficient_lifetime'
  | 'invalid_claims'
  | 'stale_in_transit'
  | 'user_mismatch';

export const ACCESS_TOKEN_EXPIRY_BUFFER_MS = 30_000;
export const TOKEN_SERVER_TIME_HEADER = 'x-digit-chat-token-server-time';

export interface TokenRequestDiagnostics {
  operation: TokenRequestOperation;
  responseReceived: boolean;
  status?: number;
  transport?: TransportFailure;
  jwtFailure?: TokenJwtFailure;
  failureAt: FailureStage;
  recovery?: TokenRecoveryDiagnostic;
}

const TOKEN_REQUEST_OPERATIONS = new Set<TokenRequestOperation>(['bootstrap', 'refresh']);
const FAILURE_STAGES = new Set<FailureStage>([
  'transport',
  'http',
  'response',
  'access_token',
  'jwt',
  'state',
  'coordination'
]);
const TRANSPORT_FAILURES = new Set<TransportFailure>([
  'watchdog_timeout',
  'cancelled',
  'no_response'
]);
const JWT_FAILURES = new Set<TokenJwtFailure>([
  'expired_or_insufficient_lifetime',
  'invalid_claims',
  'stale_in_transit',
  'user_mismatch'
]);

export interface TokenResponseValidationContext {
  operation: TokenRequestOperation;
  requestElapsedMs?: number;
  requireServerTime?: boolean;
  serverTimeMs?: number;
  status?: number;
}

export interface ValidatedAccessTokenClaims extends Record<string, unknown> {
  exp: number;
  iat: number;
}

interface TokenRequestErrorContext {
  operation: TokenRequestOperation;
  watchdogTimedOut: boolean;
}

const TOKEN_REQUEST_DIAGNOSTICS = Symbol('tokenRequestDiagnostics');
const TOKEN_REQUEST_DIAGNOSTIC_REPORTED = Symbol('tokenRequestDiagnosticReported');
const TOKEN_RESPONSE_LIFETIME = Symbol('tokenResponseLifetime');

type ErrorWithTokenRequestDiagnostics = Error & {
  [TOKEN_REQUEST_DIAGNOSTICS]?: TokenRequestDiagnostics;
  [TOKEN_REQUEST_DIAGNOSTIC_REPORTED]?: boolean;
  status?: number;
  code?: string;
};

interface ResponseWithTokenLifetime extends Record<string, unknown> {
  [TOKEN_RESPONSE_LIFETIME]?: {
    measuredAtSharedMonotonicMs: number;
    remainingLifetimeMs: number;
  };
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isValidNumericDate = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

const getStatus = (status: unknown): number | undefined =>
  typeof status === 'number' && Number.isInteger(status) && status >= 100 && status <= 599
    ? status
    : undefined;

export const createTokenRequestDiagnosticSummary = (
  value: unknown
): TokenRequestDiagnostics | undefined => {
  if (!isRecord(value)) return undefined;

  const { operation, responseReceived, failureAt, status, transport, jwtFailure } = value;

  if (
    typeof operation !== 'string' ||
    !TOKEN_REQUEST_OPERATIONS.has(operation as TokenRequestOperation) ||
    typeof responseReceived !== 'boolean' ||
    typeof failureAt !== 'string' ||
    !FAILURE_STAGES.has(failureAt as FailureStage) ||
    (status !== undefined && getStatus(status) === undefined) ||
    (transport !== undefined &&
      (typeof transport !== 'string' || !TRANSPORT_FAILURES.has(transport as TransportFailure))) ||
    (jwtFailure !== undefined &&
      (typeof jwtFailure !== 'string' || !JWT_FAILURES.has(jwtFailure as TokenJwtFailure)))
  ) {
    return undefined;
  }

  const recovery = sanitizeTokenRecoveryDiagnostic(value.recovery);
  return {
    operation: operation as TokenRequestOperation,
    responseReceived,
    ...(status !== undefined && { status: getStatus(status) }),
    ...(transport !== undefined && { transport: transport as TransportFailure }),
    ...(jwtFailure !== undefined && { jwtFailure: jwtFailure as TokenJwtFailure }),
    failureAt: failureAt as FailureStage,
    ...(recovery && { recovery })
  };
};

const getFinitePositiveNumber = (value: unknown): number | undefined => {
  const numberValue = typeof value === 'number' ? value : Number(value);

  return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : undefined;
};

const getFiniteNonNegativeNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : undefined;

export const getMonotonicElapsedMs = (startedAtMs: number, endedAtMs = performance.now()): number =>
  Math.max(0, endedAtMs - startedAtMs);

export const getTokenServerTimeFromHeaders = (headers: unknown): number | undefined => {
  if (!headers || typeof headers !== 'object') {
    return undefined;
  }

  const headersWithGetter = headers as { get?: (name: string) => unknown };
  const headerValue =
    typeof headersWithGetter.get === 'function'
      ? headersWithGetter.get(TOKEN_SERVER_TIME_HEADER)
      : (headers as Record<string, unknown>)[TOKEN_SERVER_TIME_HEADER];

  return getFinitePositiveNumber(headerValue);
};

export const attachTokenRequestDiagnostics = (
  error: Error,
  diagnostics: TokenRequestDiagnostics
): ErrorWithTokenRequestDiagnostics => {
  Object.defineProperty(error, TOKEN_REQUEST_DIAGNOSTICS, {
    configurable: false,
    enumerable: false,
    value: diagnostics,
    writable: false
  });

  if (diagnostics.status !== undefined) {
    Object.defineProperty(error, 'status', {
      configurable: false,
      enumerable: false,
      value: diagnostics.status,
      writable: false
    });
  }

  return error;
};

const createRequestError = (
  diagnostics: TokenRequestDiagnostics
): ErrorWithTokenRequestDiagnostics => {
  const error: ErrorWithTokenRequestDiagnostics = new Error('Digit Chat token request failed');
  error.name = 'DigitChatTokenRequestError';

  // Keep transport semantics available to recovery callers without retaining
  // the original request error or exposing diagnostic metadata to domain code.
  if (diagnostics.transport === 'watchdog_timeout') {
    error.code = 'ETIMEDOUT';
  } else if (diagnostics.transport === 'no_response') {
    error.code = 'ERR_NETWORK';
  } else if (diagnostics.transport === 'cancelled') {
    error.code = 'ERR_CANCELED';
  }

  return attachTokenRequestDiagnostics(error, diagnostics);
};

const createValidationError = (
  diagnostics: TokenRequestDiagnostics
): ErrorWithTokenRequestDiagnostics => {
  const error = new Error('Digit Chat token response is unusable');
  error.name = 'InvalidAccessTokenError';

  return attachTokenRequestDiagnostics(error, diagnostics);
};

const createValidationDiagnostics = (
  context: TokenResponseValidationContext,
  failureAt: Extract<FailureStage, 'response' | 'access_token' | 'jwt'>,
  jwtFailure?: TokenJwtFailure
): TokenRequestDiagnostics => ({
  operation: context.operation,
  responseReceived: true,
  ...(getStatus(context.status) !== undefined && { status: getStatus(context.status) }),
  ...(jwtFailure && { jwtFailure }),
  failureAt
});

export const decodeAndValidateAccessToken = (accessToken: string): ValidatedAccessTokenClaims => {
  const decodedToken = jwtDecode<Record<string, unknown>>(accessToken);
  const { exp, iat } = decodedToken;

  if (!isValidNumericDate(iat) || !isValidNumericDate(exp) || exp <= iat) {
    const error = new Error('Invalid access token lifetime claims');
    error.name = 'InvalidAccessTokenClaimsError';
    throw error;
  }

  return decodedToken as ValidatedAccessTokenClaims;
};

export const validateTokenResponse = (
  body: unknown,
  context: TokenResponseValidationContext
): GetTokenResponse => {
  if (!isRecord(body)) {
    throw createValidationError(createValidationDiagnostics(context, 'response'));
  }

  const { accessToken } = body;

  if (typeof accessToken !== 'string' || !accessToken.trim()) {
    throw createValidationError(createValidationDiagnostics(context, 'access_token'));
  }

  let claims: ValidatedAccessTokenClaims;

  try {
    claims = decodeAndValidateAccessToken(accessToken);
  } catch (_error) {
    throw createValidationError(createValidationDiagnostics(context, 'jwt', 'invalid_claims'));
  }

  const serverTimeMs = getFinitePositiveNumber(context.serverTimeMs);
  const requestElapsedMs = getFiniteNonNegativeNumber(context.requestElapsedMs);

  if (context.requireServerTime && (!serverTimeMs || requestElapsedMs === undefined)) {
    throw createValidationError(createValidationDiagnostics(context, 'response'));
  }

  const tokenResponse = { ...body };

  if (serverTimeMs && requestElapsedMs !== undefined) {
    const claimedLifetimeMs = (claims.exp - claims.iat) * 1000;
    const serverRemainingLifetimeMs = claims.exp * 1000 - serverTimeMs;
    const remainingLifetimeAtServerMs = Math.min(claimedLifetimeMs, serverRemainingLifetimeMs);
    const remainingLifetimeMs = remainingLifetimeAtServerMs - requestElapsedMs;

    if (
      !Number.isFinite(remainingLifetimeAtServerMs) ||
      remainingLifetimeAtServerMs <= ACCESS_TOKEN_EXPIRY_BUFFER_MS
    ) {
      throw createValidationError(
        createValidationDiagnostics(context, 'jwt', 'expired_or_insufficient_lifetime')
      );
    }

    if (
      !Number.isFinite(remainingLifetimeMs) ||
      remainingLifetimeMs <= ACCESS_TOKEN_EXPIRY_BUFFER_MS
    ) {
      throw createValidationError(createValidationDiagnostics(context, 'jwt', 'stale_in_transit'));
    }

    Object.defineProperty(tokenResponse, TOKEN_RESPONSE_LIFETIME, {
      configurable: false,
      enumerable: false,
      value: {
        measuredAtSharedMonotonicMs: performance.timeOrigin + performance.now(),
        remainingLifetimeMs
      },
      writable: false
    });
  }

  return tokenResponse as unknown as GetTokenResponse;
};

export const getTokenResponseLifetime = (
  tokenResponse: unknown
): { measuredAtSharedMonotonicMs: number; remainingLifetimeMs: number } | undefined => {
  if (!isRecord(tokenResponse)) {
    return undefined;
  }

  return (tokenResponse as ResponseWithTokenLifetime)[TOKEN_RESPONSE_LIFETIME];
};

export const getTokenRequestDiagnostics = (error: unknown): TokenRequestDiagnostics | undefined => {
  if (!(error instanceof Error)) return undefined;

  return (error as ErrorWithTokenRequestDiagnostics)[TOKEN_REQUEST_DIAGNOSTICS];
};

export const attachTokenRecoveryDiagnostics = (
  error: unknown,
  recovery: TokenRecoveryDiagnostic
): void => {
  const diagnostics = getTokenRequestDiagnostics(error);
  if (diagnostics) {
    diagnostics.recovery = { ...recovery };
  }
};

const isCancellationError = (error: unknown): boolean => {
  if (!isRecord(error)) return false;

  return (
    error.name === 'AbortError' || error.name === 'CanceledError' || error.code === 'ERR_CANCELED'
  );
};

const classifyTransportFailure = (error: unknown, watchdogTimedOut: boolean): TransportFailure => {
  if (watchdogTimedOut) return 'watchdog_timeout';
  if (isCancellationError(error)) return 'cancelled';

  return 'no_response';
};

export const annotateTokenRequestError = (
  error: unknown,
  context: TokenRequestErrorContext
): Error => {
  if (getTokenRequestDiagnostics(error)) return error as Error;

  const requestError = isRecord(error) ? error : undefined;
  const response = isRecord(requestError?.response) ? requestError.response : undefined;
  const responseReceived = Boolean(response);
  const diagnostics: TokenRequestDiagnostics = {
    operation: context.operation,
    responseReceived,
    ...(getStatus(response?.status) !== undefined && { status: getStatus(response?.status) }),
    failureAt: responseReceived ? 'http' : 'transport'
  };

  if (!responseReceived) {
    diagnostics.transport = classifyTransportFailure(error, context.watchdogTimedOut);
  }

  return createRequestError(diagnostics);
};

export const markTokenRequestDiagnosticReported = (error: unknown): void => {
  if (!(error instanceof Error)) return;

  Object.defineProperty(error, TOKEN_REQUEST_DIAGNOSTIC_REPORTED, {
    configurable: false,
    enumerable: false,
    value: true,
    writable: false
  });
};

export const wasTokenRequestDiagnosticReported = (error: unknown): boolean =>
  error instanceof Error &&
  Boolean((error as ErrorWithTokenRequestDiagnostics)[TOKEN_REQUEST_DIAGNOSTIC_REPORTED]);

export const createTokenResponseValidator =
  (operation: TokenRequestOperation) =>
  (body: unknown, context: Omit<TokenResponseValidationContext, 'operation'>) =>
    validateTokenResponse(body, { ...context, operation, requireServerTime: true });

export const createTokenErrorDecorator =
  (operation: TokenRequestOperation) =>
  (error: unknown, context: Omit<TokenRequestErrorContext, 'operation'>) =>
    annotateTokenRequestError(error, { ...context, operation });
